import type * as React from 'react';

import SvgAccount from './SvgAccount';
import SvgAccountFull from './SvgAccountFull';
import SvgAI from './SvgAI';
import SvgAlertCircle from './SvgAlertCircle';
import SvgAmericanFootball from './SvgAmericanFootball';
import SvgAnalysis from './SvgAnalysis';
import SvgArrowBack from './SvgArrowBack';
import SvgArrowBackCircle from './SvgArrowBackCircle';
import SvgArrowDown from './SvgArrowDown';
import SvgArrowDownCircle from './SvgArrowDownCircle';
import SvgArrowForward from './SvgArrowForward';
import SvgArrowForwardCircle from './SvgArrowForwardCircle';
import SvgArrowUp from './SvgArrowUp';
import SvgArrowUpCircle from './SvgArrowUpCircle';
import SvgAthletics from './SvgAthletics';
import SvgAussieRules from './SvgAussieRules';
import SvgAZ from './SvgAZ';
import SvgBadminton from './SvgBadminton';
import SvgBaseball from './SvgBaseball';
import SvgBasketball from './SvgBasketball';
import SvgBetslip from './SvgBetslip';
import SvgBettingSites from './SvgBettingSites';
import SvgBettingSplits from './SvgBettingSplits';
import SvgBowls from './SvgBowls';
import SvgBoxing from './SvgBoxing';
import SvgBurgerMenu from './SvgBurgerMenu';
import SvgCasino from './SvgCasino';
import SvgCombatSports from './SvgCombatSports';
import SvgCricket from './SvgCricket';
import SvgCrypto from './SvgCrypto';
import SvgCup from './SvgCup';
import SvgCycling from './SvgCycling';
import SvgDarts from './SvgDarts';
import SvgDiscord from './SvgDiscord';
import SvgEducation from './SvgEducation';
import SvgESports from './SvgESports';
import SvgEyes from './SvgEyes';
import SvgEyesOff from './SvgEyesOff';
import SvgFilter from './SvgFilter';
import SvgFilterSliders from './SvgFilterSliders';
import SvgFinance from './SvgFinance';
import SvgGolf from './SvgGolf';
import SvgGreyhounds from './SvgGreyhounds';
import SvgHandball from './SvgHandball';
import SvgHitRate from './SvgHitRate';
import SvgHome from './SvgHome';
import SvgHorseRacing from './SvgHorseRacing';
import SvgIceHockey from './SvgIceHockey';
import SvgInfoCircle from './SvgInfoCircle';
import SvgInsights from './SvgInsights';
import SvgLacrosse from './SvgLacrosse';
import SvgLogOut from './SvgLogOut';
import SvgMatchups from './SvgMatchups';
import SvgMotorsport from './SvgMotorsport';
import SvgNewWindow from './SvgNewWindow';
import SvgNoWifi from './SvgNoWifi';
import SvgOffersFull from './SvgOffersFull';
import SvgOffersOutline from './SvgOffersOutline';
import SvgPadlock from './SvgPadlock';
import SvgPolitics from './SvgPolitics';
import SvgPool from './SvgPool';
import SvgPopular from './SvgPopular';
import SvgPositiveEV from './SvgPositiveEV';
import SvgPremiumTipsUS from './SvgPremiumTipsUS';
import SvgRowing from './SvgRowing';
import SvgRugby from './SvgRugby';
import SvgSearch from './SvgSearch';
import SvgSnooker from './SvgSnooker';
import SvgSoccer from './SvgSoccer';
import SvgSort from './SvgSort';
import SvgSquash from './SvgSquash';
import SvgStarFull from './SvgStarFull';
import SvgStarOutline from './SvgStarOutline';
import SvgTableTennis from './SvgTableTennis';
import SvgTennis from './SvgTennis';
import SvgTickCircle from './SvgTickCircle';
import SvgTipsFire from './SvgTipsFire';
import SvgTools from './SvgTools';
import SvgTrash from './SvgTrash';
import SvgTrends from './SvgTrends';
import SvgTV from './SvgTV';
import SvgVolleyball from './SvgVolleyball';
import SvgWinterSports from './SvgWinterSports';
import SvgXCross from './SvgXCross';

/**
 * The UK icon set, keyed by the name callers ask for — the API's `icon` value for sports, our own
 * spelling for hard-coded chrome. Deliberately NOT the Figma layer name: the API's names needed a
 * resolver to reach Figma's, and the lookup is a plain own-property hit instead. Where the API's word
 * differs from the artwork's (`Football` for the soccer ball), the API's word is the key and a second
 * key may share a component. Chrome icons the API never sends keep their Figma spelling — they were
 * always exact hits, so renaming them would churn call sites for nothing.
 *
 * An object literal rather than a re-export barrel like `icons/index.ts`: `A-Z` and `E-Sports` are not
 * valid JavaScript identifiers.
 */
export const ukIcons: Record<string, React.ComponentType<React.SVGProps<SVGSVGElement>>> = {
  'A-Z': SvgAZ,
  Account: SvgAccount,
  Account_Full: SvgAccountFull,
  AI: SvgAI,
  Alert_Circle: SvgAlertCircle,
  AmericanFootball: SvgAmericanFootball,
  Analysis: SvgAnalysis,
  Arrow_Back: SvgArrowBack,
  Arrow_Back_Circle: SvgArrowBackCircle,
  Arrow_Down: SvgArrowDown,
  Arrow_Down_Circle: SvgArrowDownCircle,
  Arrow_Forward: SvgArrowForward,
  Arrow_Forward_Circle: SvgArrowForwardCircle,
  Arrow_Up: SvgArrowUp,
  Arrow_Up_Circle: SvgArrowUpCircle,
  Athletics: SvgAthletics,
  AussieRules: SvgAussieRules,
  Badminton: SvgBadminton,
  Baseball: SvgBaseball,
  Basketball: SvgBasketball,
  Betslip: SvgBetslip,
  BettingSites: SvgBettingSites,
  BettingSplits: SvgBettingSplits,
  Bowls: SvgBowls,
  Boxing: SvgBoxing,
  Burger_Menu: SvgBurgerMenu,
  Casino: SvgCasino,
  CombatSports: SvgCombatSports,
  Cricket: SvgCricket,
  Crypto: SvgCrypto,
  Cup: SvgCup,
  Cycling: SvgCycling,
  Darts: SvgDarts,
  Discord: SvgDiscord,
  'E-Sports': SvgESports,
  Education: SvgEducation,
  Eyes: SvgEyes,
  Eyes_Off: SvgEyesOff,
  Filter: SvgFilter,
  Filter_Sliders: SvgFilterSliders,
  Finance: SvgFinance,
  Football: SvgSoccer,
  Golf: SvgGolf,
  Greyhounds: SvgGreyhounds,
  Handball: SvgHandball,
  HitRate: SvgHitRate,
  Hockey: SvgIceHockey,
  Home: SvgHome,
  HorseRacing: SvgHorseRacing,
  IceHockey: SvgIceHockey,
  Info_Circle: SvgInfoCircle,
  Insights: SvgInsights,
  Lacrosse: SvgLacrosse,
  Log_Out: SvgLogOut,
  Matchups: SvgMatchups,
  Motorsport: SvgMotorsport,
  MotorSport: SvgMotorsport,
  New_Window: SvgNewWindow,
  No_Wifi: SvgNoWifi,
  OffersFull: SvgOffersFull,
  OffersOutline: SvgOffersOutline,
  Padlock: SvgPadlock,
  Politics: SvgPolitics,
  Pool: SvgPool,
  Popular: SvgPopular,
  PositiveEV: SvgPositiveEV,
  PremiumTipsUS: SvgPremiumTipsUS,
  Rowing: SvgRowing,
  Rugby: SvgRugby,
  RugbyLeague: SvgRugby,
  Search: SvgSearch,
  Snooker: SvgSnooker,
  Soccer: SvgSoccer,
  Sort: SvgSort,
  Specials: SvgStarOutline,
  Squash: SvgSquash,
  StarFull: SvgStarFull,
  StarOutline: SvgStarOutline,
  TableTennis: SvgTableTennis,
  Tennis: SvgTennis,
  Tick_Circle: SvgTickCircle,
  TipsFire: SvgTipsFire,
  Tools: SvgTools,
  Trash: SvgTrash,
  Trends: SvgTrends,
  TV: SvgTV,
  UfcMma: SvgCombatSports,
  Volleyball: SvgVolleyball,
  WinterSports: SvgWinterSports,
  X_Cross: SvgXCross,
};
